<template>
	<section class="mb-4 lg:mb-8">
		<div
			class="flex flex-col gap-4 lg:gap-5 border-b border-slate-100 pb-2 lg:pb-6"
		>
			<Subtitle
				weight="medium"
				class="title-font"
			>
				Market Trends
			</Subtitle>
			<div class="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
				<TabButton
					v-for="tab in tabs"
					:key="tab.value"
					@click="handleTabChange(tab.value)"
					:active="activeTab === tab.value"
					:class="[
						'rounded lg:px-3 lg:py-1 text-xs font-semibold focus:outline-none transition border',
						activeTab === tab.value
							? 'bg-yellow-500 text-black border-yellow-500 shadow'
							: 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
					]"
				>
					{{ tab.label }}
				</TabButton>
			</div>
		</div>
		<!-- Loading state -->
		<MarketTrendsSkeleton v-if="loading" />

		<!-- Content when loaded -->
		<div
			class="relative"
			v-else-if="news.length > 0"
		>
			<div
				:class="[
					'flex flex-col text-sm',

					{
						'max-h-[600px]  overflow-hidden': news.length > 4 && !showAll
					}
				]"
			>
				<div
					v-for="item in news"
					:key="item.slug"
					:class="[
						'flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3 py-4 border-b border-slate-200'
					]"
				>
					<div class="w-[120px]">
						<ClientOnly>
							<TickerBadge
								v-if="
									item.stock_pairs &&
									item.stock_pairs[1] &&
									stocksStore.getStockSubscription(item.stock_pairs[1])
								"
								:symbol="stocksStore.getStockSubscription(item.stock_pairs[1])?.SYMBOL"
								:change="
									String(
										stocksStore
											.getStockSubscription(item.stock_pairs[1])
											?.PC_CHG?.toFixed(2) || '0'
									) + '%'
								"
							/>
						</ClientOnly>
					</div>

					<div class="w-[220px]">
						<NuxtLink
							:to="`/news/${item.slug || ''}/`"
							class="text-base font-bold text-black hover:underline line-clamp-2 leading-normal"
						>
							{{ he.decode(item.title || "") }}
						</NuxtLink>
					</div>
					<NuxtLink
						:to="`/news/${item.slug || ''}/`"
						class="flex flex-col max-w-[520px]"
					>
						<span
							class="text-sm lg:text-base text-black hover:underline line-clamp-2 mb-2 lg:mb-0"
						>
							{{ stripHtml(he.decode(item.content || "")) }}
						</span>
						<span class="text-xs lg:text-sm text-slate-400">
							<RelativeTime :date="item.lastmod || item.date" />
						</span>
					</NuxtLink>
				</div>
			</div>

			<!-- White Gradient Overlay - Mobile Only -->

			<ShowMore
				@click="toggleShowAll"
				v-if="news.length > 3 && !showAll"
				variant="primary"
				class="lg:hidden mt-6"
			/>
		</div>

		<!-- Empty state with reserved height to prevent layout shift -->
		<div
			v-else
			class="flex items-center justify-center text-sm text-center text-gray-400 min-h-[300px]"
		>
			No data available at the moment, check again later.
		</div>
	</section>
</template>

<script setup>
import { ref, onMounted, computed, watchEffect } from "vue";
import axios from "axios";
import he from "he";
import { useStocksStore } from "~/stores/useStocksStore";

import MarketTrendsSkeleton from "~/components/skeleton/MarketTrendsSkeleton.vue";

// Strip HTML tags and decode HTML entities to get plain text
const stripHtml = (html) => {
	if (!html) return "";
	return he.decode(html.replace(/<[^>]*>/g, ""));
};

const props = defineProps({
	// Pre-fetched { most_active, top_gainers, top_losers } bundle from the
	// flattened `news-feed/trends` endpoint (fetched once by the parent
	// NewsMarkets.vue). When a tab's data is present here, switching tabs is
	// instant (no network call). Falls back to the original per-tab
	// `stocks/news/filtered` fetch when null/missing/empty for a given tab.
	initialTrends: {
		type: Object,
		default: null
	}
});

const stocksStore = useStocksStore();
const showAll = ref(false);

// Component state
const loading = ref(false);
const activeTab = ref("most_active");

// Get runtime config
const $config = useRuntimeConfig();
const { songbirdProxy } = $config.public;

const fetchFilteredNews = async (filter) => {
	// On the server use the private Songbird URL directly to avoid self-referential
	// subrequests in Cloudflare Workers. On the client use the public proxy.
	const base = import.meta.server ? $config.songbirdUrl + "api/" : songbirdProxy;

	const headers = { "X-Original-Origin": $config.public.current_hostname };

	try {
		const response = await axios.get(`${base}stocks/news/filtered`, {
			params: { value: filter, is_sector: false },
			headers,
			timeout: import.meta.server ? 8000 : 15000
		});
		const responseData = response?.data;
		if (!responseData || typeof responseData !== "object") return [];
		return Object.keys(responseData)
			.map((key) => {
				const story = responseData[key]?.[0];
				if (!story) return null;
				return {
					...story,
					title: story.title || "",
					content: story.content || "",
					slug: story.slug || "",
					stock_pairs: story.stock_pairs || [],
					date: story.date || "",
					lastmod: story.lastmod || story.date || ""
				};
			})
			.filter(Boolean);
	} catch (err) {
		console.warn("[MarketsTrends] Failed to fetch data:", err?.message || err);
		return [];
	}
};

const TAB_FILTER_MAP = {
	most_active: "most_active",
	gainers: "top_gainers",
	losers: "top_losers"
};

/** Read a tab's stories from the bundled `initialTrends` prop, if present. */
const bundledStoriesFor = (filter) => {
	const bundled = props.initialTrends?.[filter];
	return Array.isArray(bundled) && bundled.length > 0 ? bundled : null;
};

// Prefer the bundled, parameter-free feed passed down from NewsMarkets.vue.
// Only fall back to the original per-tab SSR fetch when the bundle is
// missing/empty for the default tab (e.g. the flattened endpoint failed).
const bundledMostActive = bundledStoriesFor("most_active");
const { data: initialTrendsData } = await useAsyncData(
	"markets-trends-most-active",
	() => (bundledMostActive ? Promise.resolve(bundledMostActive) : fetchFilteredNews("most_active"))
);

const news = ref(bundledMostActive ?? initialTrendsData.value ?? []);

const tabs = [
	{ label: "Most Active", value: "most_active" },
	{ label: "Gainers", value: "gainers" },
	{ label: "Losers", value: "losers" }
];

// Extract stock symbols from news items and trigger data fetching
const stockSymbols = computed(() => {
	if (!news.value || news.value.length === 0) {
		return [];
	}
	return news.value
		.filter((item) => item.stock_pairs && item.stock_pairs[1])
		.map((item) => item.stock_pairs[1])
		.filter(Boolean);
});

// Watch for stock symbols and update subscribed symbols
watchEffect(() => {
	if (stockSymbols.value.length > 0) {
		// Add these symbols to the subscribed set which will trigger automatic fetching
		stockSymbols.value.forEach((symbol) => {
			stocksStore.stock_subscribed_symbols.add(symbol);
		});
	}
});

// Handle tab change (client-side tab switches after SSR)
const handleTabChange = async (tab) => {
	activeTab.value = tab;
	const filter = TAB_FILTER_MAP[tab];
	if (!filter) return;

	// Instant switch when the parent already handed us this tab's data via
	// the bundled news-feed/trends fetch — no network round-trip needed.
	const bundled = bundledStoriesFor(filter);
	if (bundled) {
		news.value = bundled;
		return;
	}

	loading.value = true;
	news.value = await fetchFilteredNews(filter);
	loading.value = false;
};

const toggleShowAll = () => {
	showAll.value = !showAll.value;
};

// SSR already loaded "most_active". Only re-fetch on client if SSR returned nothing.
onMounted(async () => {
	if (news.value.length === 0) {
		await handleTabChange("most_active");
	}
});
</script>

<style scoped>
.scrollbar-hide {
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
}
.scrollbar-hide::-webkit-scrollbar {
	display: none; /* Chrome, Safari, and Opera */
}

/* Show More button styling */
button {
	transition: all 0.2s ease-in-out;
}

button:hover {
	transform: translateY(-1px);
}

button:active {
	transform: scale(0.95);
}

/* Mobile-specific improvements */
@media (max-width: 1023px) {
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
}
</style>
